import type { AxiosInstance } from "axios"
import { DateTime } from "luxon"
import { toast } from "react-toastify"
import handleDownloadResult from "./util/handleDownloadResult"
import handleQueryResult from "./util/handleQueryResult"
import PhinExceptionTypeKey from "mco-keys/src/t4/PhinExceptionTypeKey"
import { getFormattedDate } from "../../services/DateHelpers"

class CadoganExtractsClient {
	constructor(private axiosClient: AxiosInstance) {}

	public startAssessmentExtract = () =>
		this.axiosClient.get("/api/admin/extracts/cadogan/extract")
	public startOverdueBenchmarkExtract = () =>
		this.axiosClient.get(
			"/api/admin/extracts/cadogan/extract/overduebenchmark"
		)
}

class CircleExtractsClient {
	constructor(private axiosClient: AxiosInstance) {}

	public startDailyExtract = async (fromDate: DateTime<true>) => {
		const query = new URLSearchParams()
		query.append("fromDate", getFormattedDate(fromDate))
		await this.axiosClient.get(
			`/api/admin/extracts/circle/extract/daily?${query.toString()}`
		)
	}

	public startWorsenedPatientsExtract = async (
		fromDate: DateTime<true>,
		toDate: DateTime
	) => {
		const query = new URLSearchParams()
		query.append("fromDate", getFormattedDate(fromDate))
		query.append("toDate", getFormattedDate(toDate))
		await this.axiosClient.get(
			`/api/admin/extracts/circle/extract/worsenedPatients?${query.toString()}`
		)
	}

	public startMissingClientPatientIdExtract = async () => {
		await this.axiosClient.get(
			"/api/admin/extracts/circle/extract/missingtenantid"
		)
	}
}

class HcaExtractsClient {
	constructor(private axiosClient: AxiosInstance) {}

	public startDailyExtract = async (fromDate: DateTime<true>) => {
		const query = new URLSearchParams()
		query.append("fromDate", getFormattedDate(fromDate))
		await this.axiosClient.get(
			`/api/admin/extracts/hca/extract/daily?${query.toString()}`
		)
	}
}

class LillyBreastCancerExtractsClient {
	constructor(private axiosClient: AxiosInstance) {}

	public startAssessmentExtract = async (body: {
		fromDate: DateTime
		toDate: DateTime
		conditionKeys: string[]
		treatmentCenterUuids: string[]
	}) => {
		await this.axiosClient.post(
			`/api/admin/extracts/lillybreastcancer/extract/assessment`,
			body
		)
	}
}

class LillyIbdExtractsClient {
	constructor(private axiosClient: AxiosInstance) {}

	public startAssessmentExtract = async (body: {
		fromDate: DateTime
		toDate: DateTime
		conditionKeys: string[]
		treatmentCenterUuids: string[]
	}) => {
		await this.axiosClient.post(
			`/api/admin/extracts/lillyibd/extract/assessment`,
			body
		)
	}
}

class NhsEnglandExtractsClient {
	constructor(private axiosClient: AxiosInstance) {}

	public startExtract = (request: {
		conditionKeys: string[]
		benchmarkKeys: string[]
		from: DateTime
		to: DateTime
		includeCsv: boolean
	}) => this.axiosClient.post<void>(`/api/admin/extracts/nhsengland`, request)
}

type PhinDigest = { name: string; createDate: string; count: number }
type PhinExceptionTypesResponse = {
	key: PhinExceptionTypeKey
	errorMessage: string
	total: number
}
export type PhinExceptionReport = {
	exceptionFile: string
	episodeNumbers: {
		episodeNumber: string
		matchedPatientConditionUserId?: string
	}[]
	exceptionTypeKey?: PhinExceptionTypeKey
	errorMessage: string
	errorDescription?: string
}
type PhinException = {
	phinExceptionId: number
	recordId: string
	exceptionFileName: string
	episodeNumber: string
	matchedUserId?: string
	errorLevel: string
	errorTypeKey: PhinExceptionTypeKey
	errorMessage: string
	errorFieldName: string
	errorFieldValue: string
	fileRecord: unknown
	relatedExceptions: string[]
}
type PhinExceptionResults = {
	data: PhinException[]
	total: number
	totalFiltered: number
}
class PhinExtractsClient {
	constructor(private axiosClient: AxiosInstance) {}

	public getDigests = () =>
		handleQueryResult(
			this.axiosClient.get<PhinDigest[]>(
				"/api/admin/extracts/phin/digests"
			)
		)

	public undoDigest = (createDate: string) =>
		handleQueryResult(
			this.axiosClient.delete<void>(
				`/api/admin/extracts/digest?date=${createDate}`
			)
		)

	public startSendToSftp = () =>
		this.axiosClient.get<void>("/api/admin/extracts/phin")

	public getExceptionFileDates = () =>
		this.axiosClient.get<string[]>("/api/admin/phin/exceptions/dates")
	public getExceptionTypes = (fileDateFilter: string) =>
		this.axiosClient.get<PhinExceptionTypesResponse[]>(
			`/api/admin/phin/exceptions/types?fileDateFilter=${fileDateFilter}`
		)
	public getExceptions = ({
		page,
		pageSize,
		episodeNumberFilter,
		exceptionTypeFilter,
		fileDateFilter
	}: {
		page: number
		pageSize: number
		episodeNumberFilter: string
		exceptionTypeFilter: PhinExceptionTypeKey | string
		fileDateFilter: string
	}) => {
		const skip = (page - 1) * pageSize
		const query = new URLSearchParams()

		query.append("skip", skip.toString())
		query.append("take", pageSize.toString())
		query.append("fileDateFilter", fileDateFilter)
		if (exceptionTypeFilter) {
			query.append("exceptionTypeFilter", exceptionTypeFilter)
		}
		if (episodeNumberFilter) {
			query.append("episodeNumberFilter", episodeNumberFilter)
		}

		return this.axiosClient.get<PhinExceptionResults>(
			`/api/admin/phin/exceptions?${query.toString()}`
		)
	}
	public getExceptionReport = ({
		episodeNumberFilter,
		fileDateFilter,
		exceptionTypeFilter
	}: {
		fileDateFilter: string
		episodeNumberFilter?: string
		exceptionTypeFilter?: string | PhinExceptionTypeKey
	}) => {
		const query = new URLSearchParams()
		query.append("sourceFileDate", fileDateFilter)
		if (episodeNumberFilter) {
			query.append("episodeNumberFilter", episodeNumberFilter)
		}
		if (exceptionTypeFilter) {
			query.append("exceptionTypeFilter", exceptionTypeFilter)
		}
		return this.axiosClient.get<PhinExceptionReport[]>(
			`/api/admin/phin/exceptions/report?${query.toString()}`
		)
	}

	public deleteExceptions = () =>
		this.axiosClient.delete<void>("/api/admin/phin/exceptions")
	public uploadExceptions = (file: File) =>
		this.axiosClient.postForm<void>("/api/admin/phin/exceptions", {
			files: [file]
		})

	public startMissingInformationExtract = (from: DateTime, to: DateTime) => {
		const query = new URLSearchParams()
		query.append("registrationDateFrom", from.toFormat("yyyy-MM-dd"))
		query.append("registrationDateTo", to.toFormat("yyyy-MM-dd"))
		this.axiosClient.get<void>(
			`/api/admin/extracts/phinmissinginformation?${query.toString()}`
		)
	}

	public uploadMissingInformation = (file: File) =>
		this.axiosClient.postForm<void>(
			"/api/admin/import/phinmissinginformation",
			{ files: [file] }
		)
}

class SpireExtractsClient {
	constructor(private axiosClient: AxiosInstance) {}

	public startAdHocExtract = async (body: {
		treatmentCenterUuid: string
		conditionKey: string
		fromDate: DateTime
		toDate: DateTime
	}) => {
		await this.axiosClient.post(
			`/api/admin/extracts/spire/extract/adhoc`,
			body
		)
	}

	public downloadPatientDetailsExtract = async (
		patientConditionIds: number[]
	) => {
		const response = await this.axiosClient.post(
			`/api/admin/extracts/spire/patientdetails`,
			patientConditionIds,
			{ responseType: "blob" }
		)
		handleDownloadResult("spire_patient_details_extract.zip", response)
	}

	public startDailyExtract = async (fromDate: DateTime<true>) => {
		const query = new URLSearchParams()
		query.append("fromDate", getFormattedDate(fromDate))
		await this.axiosClient.get(
			`/api/admin/extracts/spire/extract/daily?${query.toString()}`
		)
	}

	public startMissingClientPatientIdExtract = async () => {
		await this.axiosClient.get(
			"/api/admin/extracts/spire/extract/missingclientpatientid"
		)
	}

	public downloadMissingNhsNumberExtract = async (
		from: DateTime,
		to: DateTime
	) => {
		const query = new URLSearchParams()
		query.append("dateFrom", from.toFormat("yyyy-MM-dd"))
		query.append("dateTo", to.toFormat("yyyy-MM-dd"))

		const response = await this.axiosClient.get(
			`/api/admin/extracts/spire/nhsnumbermatch?${query.toString()}`,
			{ responseType: "blob" }
		)
		handleDownloadResult("spire_nhs_number_extract.zip", response)
	}

	public downloadMissingEpisodeNumberExtract = async (
		from: DateTime<true>,
		to: DateTime<true>
	) => {
		const query = new URLSearchParams()
		query.append("registrationDateFrom", from.toFormat("yyyy-MM-dd"))
		query.append("registrationDateTo", to.toFormat("yyyy-MM-dd"))
		const response = await this.axiosClient.get(
			`/api/admin/extracts/spire/extract/missingepisodenumber?${query.toString()}`,
			{ responseType: "blob" }
		)
		handleDownloadResult(
			"spire_missing_episode_number_extract.zip",
			response
		)
	}

	public startWeeklyExtract = async (from: DateTime<true>) => {
		const query = new URLSearchParams()
		query.append("fromDate", getFormattedDate(from))
		await this.axiosClient.get(
			`/api/admin/extracts/spire/extract?${query.toString()}`
		)
	}

	public startWorsenedPatientsExtract = async (fromDate: DateTime<true>) => {
		const query = new URLSearchParams()
		query.append("fromDate", getFormattedDate(fromDate))
		await this.axiosClient.get(
			`/api/admin/extracts/spire/extract/worsenedPatients?${query.toString()}`
		)
	}
}

class StJosephsExtractsClient {
	constructor(private axiosClient: AxiosInstance) {}

	public startWorsenedPatientsExtract = async (fromDate: DateTime<true>) => {
		const query = new URLSearchParams()
		query.append("fromDate", getFormattedDate(fromDate))
		await this.axiosClient.get(
			`/api/admin/extracts/stjosephs/extract/worsenedPatients?${query.toString()}`
		)
	}
}

class TlcExtractsClient {
	constructor(private axiosClient: AxiosInstance) {}

	public startWeeklyExtract = async (fromDate: DateTime<true>) => {
		const query = new URLSearchParams()
		query.append("fromDate", getFormattedDate(fromDate))
		await this.axiosClient.get(
			`/api/admin/extracts/tlc/extract/weekly?${query.toString()}`
		)
	}
	public startWorsenedPatientsExtract = async (fromDate: DateTime<true>) => {
		const query = new URLSearchParams()
		query.append("fromDate", getFormattedDate(fromDate))
		await this.axiosClient.get(
			`/api/admin/extracts/tlc/extract/worsenedPatients?${query.toString()}`
		)
	}
}

export class ExtractsClient {
	constructor(private axiosClient: AxiosInstance) {
		this.cadogan = new CadoganExtractsClient(axiosClient)
		this.circle = new CircleExtractsClient(axiosClient)
		this.hcauk = new HcaExtractsClient(axiosClient)
		this.lillyepromsbreastcancer = new LillyBreastCancerExtractsClient(
			axiosClient
		)
		this.lillyepromsibd = new LillyIbdExtractsClient(axiosClient)
		this.nhsEngland = new NhsEnglandExtractsClient(axiosClient)
		this.phin = new PhinExtractsClient(axiosClient)
		this.spire = new SpireExtractsClient(axiosClient)
		this.stjosephs = new StJosephsExtractsClient(axiosClient)
		this.tlc = new TlcExtractsClient(axiosClient)
	}

	public cadogan: CadoganExtractsClient
	public circle: CircleExtractsClient
	public hcauk: HcaExtractsClient
	public lillyepromsbreastcancer: LillyBreastCancerExtractsClient
	public lillyepromsibd: LillyIbdExtractsClient
	public nhsEngland: NhsEnglandExtractsClient
	public phin: PhinExtractsClient
	public spire: SpireExtractsClient
	public stjosephs: StJosephsExtractsClient
	public tlc: TlcExtractsClient

	public startConsultantEmailsExtract = async () => {
		await this.axiosClient.get("/api/admin/extracts/consultantemails")
	}

	public startConsultantMonthlyPatientScoreExtract = async (
		fromDate: DateTime<true>,
		toDate: DateTime<true>
	) => {
		const query = new URLSearchParams()
		query.append("fromDate", getFormattedDate(fromDate))
		query.append("toDate", getFormattedDate(toDate))
		await this.axiosClient.get(
			`/api/admin/extracts/consultantmonthlypatientscore?${query.toString()}`
		)
	}

	public startFinalExtract = async (includeNonConsenting: boolean) => {
		const query = new URLSearchParams()
		query.append("includeNonConsenting", `${includeNonConsenting}`)
		await this.axiosClient.get(`/api/admin/extracts/final?${query}`)
	}

	public startAdHocUriExtract = async (fromDate: DateTime<true>) => {
		const query = new URLSearchParams()
		query.append("from", `${fromDate}`)
		await this.axiosClient.get(
			`/api/admin/extracts/uri/adhoc?${query.toString()}`
		)
	}

	public downloadReportingSurveyAnswers = async (fileName: string) => {
		try {
			const response = await this.axiosClient.get(
				"/api/admin/extracts/uri/surveyanswers",
				{ responseType: "blob" }
			)
			handleDownloadResult(fileName, response)
		} catch {
			toast.error(
				"Unable to download survey, question, answer URI extract"
			)
		}
	}
}
